import Button from "@/components/Common/Button";
import useCountries from "@/hooks/useCountries";
import {usePayment} from "@/hooks/usePayment";
import {useSettingsCompanyStore} from "@/store/company";
import type {TCountry} from "@/store/types";
import {PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import type {FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {useAsyncFn} from "react-use";

const PaymentMethodFields: FC<{onClose: () => void}> = ({onClose}) => {
  const {t} = useTranslation("common");
  const company = useSettingsCompanyStore(state => state.company);
  const stripe = useStripe();
  const elements = useElements();
  const {createSetupIntent, setDefaultPaymentMethod} = usePayment({agencyId: company.agency?.id});
  const {countriesData} = useCountries();
  const billingCountry = company.country ? countriesData[company.country as TCountry]?.code : undefined;

  const [{loading: isSubmitting}, handleSave] = useAsyncFn(async () => {
    if (!stripe || !elements) return;
    const {error: submitError} = await elements.submit();
    if (submitError) {
      toast.error(submitError.message);
      return;
    }
    const clientSecret = await createSetupIntent();
    const {error, setupIntent} = await stripe.confirmSetup({
      elements,
      clientSecret,
      redirect: "if_required",
      confirmParams: {return_url: window.location.href},
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    if (typeof setupIntent?.payment_method === "string") {
      await setDefaultPaymentMethod({paymentMethodId: setupIntent.payment_method});
    }
    onClose();
  }, [stripe, elements, createSetupIntent, setDefaultPaymentMethod, onClose]);

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      <PaymentElement options={{defaultValues: {billingDetails: {address: {country: billingCountry}}}}} />
      <div className="flex flex-row justify-end gap-2">
        <Button isDisabled={isSubmitting} className="h-8 self-end bg-gray-200 px-3" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          isDisabled={isSubmitting || !stripe}
          buttonStyle="Blue"
          className="h-8 self-end px-3"
          onClick={handleSave}>
          {isSubmitting ? t("loading") : t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default PaymentMethodFields;
